'use client';

import { useState } from 'react';

import { cn } from '@/lib/utils';

import { SegmentedControl } from '../../shared/segmented-control';
import { useSim } from './use-sim';

/**
 * The tier walk, played: a provider outage burns retries and hops forward
 * with zero wait; a bad prompt stops the whole chain on the first answer.
 */

type TierState = 'idle' | 'active' | 'failed' | 'success' | 'skipped';

type Frame = {
  tiers: [TierState, TierState, TierState];
  note: string;
  chip?: string;
};

const OUTAGE: Frame[] = [
  { tiers: ['active', 'idle', 'idle'], note: 'Attempt 1 at tier 1…' },
  {
    tiers: ['active', 'idle', 'idle'],
    note: 'Failed. Retry in 5s.',
    chip: 'server error',
  },
  {
    tiers: ['active', 'idle', 'idle'],
    note: 'Failed again. Retry in 10s.',
    chip: 'server error',
  },
  {
    tiers: ['failed', 'active', 'idle'],
    note: 'Tier exhausted. Hop to tier 2: 0s wait.',
    chip: 'hop · 0s',
  },
  {
    tiers: ['failed', 'success', 'idle'],
    note: 'Delivered by tier 2. Same model family, different supplier.',
    chip: 'success',
  },
];

const PROMPT: Frame[] = [
  { tiers: ['active', 'idle', 'idle'], note: 'Attempt 1 at tier 1…' },
  {
    tiers: ['failed', 'skipped', 'skipped'],
    note: 'Provider refused the prompt. Every provider would. Chain stops, user told why.',
    chip: 'content rejection · terminal',
  },
];

export function ChainSim() {
  const [scenario, setScenario] = useState<'outage' | 'prompt'>('outage');
  const frames = scenario === 'outage' ? OUTAGE : PROMPT;
  const { ref, tick, replay, reduceMotion } = useSim({
    totalTicks: frames.length + 2,
    msPerTick: 950,
    loop: true,
  });

  const frame =
    frames[
      reduceMotion ? frames.length - 1 : Math.min(tick, frames.length - 1)
    ];

  return (
    <div ref={ref} className="mt-4 border border-edge bg-muted/20 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
          one job · three tiers
        </p>
        <SegmentedControl
          label="Failure scenario"
          labelHidden
          value={scenario}
          onChange={(next) => {
            setScenario(next);
            // A new scenario must play from its first frame, not resume the
            // old clock mid-story.
            replay();
          }}
          options={[
            { value: 'outage', label: 'Provider outage' },
            { value: 'prompt', label: 'Bad prompt' },
          ]}
        />
      </div>

      <div
        className="grid grid-cols-3 gap-2 sm:gap-3"
        role="img"
        aria-label={
          scenario === 'outage'
            ? 'Simulation: retries at tier one back off five then ten seconds, then the job hops to tier two with zero wait and succeeds.'
            : 'Simulation: a content rejection at tier one stops the chain immediately; the other tiers are never tried.'
        }
      >
        {frame.tiers.map((state, index) => (
          <div
            key={`${scenario}-${String(index)}`}
            className={cn(
              'rounded-lg border p-3 transition-all duration-300',
              state === 'active' && 'border-foreground',
              state === 'success' &&
                'border-foreground bg-foreground text-background',
              state === 'failed' && 'border-edge opacity-60',
              state === 'skipped' && 'border-dashed border-edge opacity-40',
              state === 'idle' && 'border-edge'
            )}
          >
            <p className="font-mono text-[10px] tracking-wider uppercase">
              tier {index + 1}
            </p>
            <p
              className={cn(
                'mt-1 font-mono text-[11px] font-medium',
                state !== 'success' && 'text-muted-foreground'
              )}
            >
              {state === 'active' && 'trying…'}
              {state === 'success' && 'delivered'}
              {state === 'failed' &&
                (scenario === 'prompt' ? 'refused' : 'exhausted')}
              {state === 'skipped' && 'never tried'}
              {state === 'idle' && 'standing by'}
            </p>
          </div>
        ))}
      </div>

      {/* Every frame stacks in the same grid cell so the tallest one sets
          the height and the loop never resizes the widget. */}
      <div className="mt-3 grid">
        {frames.map((f) => (
          <div
            key={f.note}
            className={cn(
              'col-start-1 row-start-1 flex flex-wrap items-center gap-2',
              f !== frame && 'invisible'
            )}
            aria-hidden={f !== frame}
          >
            {f.chip && (
              <span
                className={cn(
                  'rounded-md px-1.5 py-0.5 font-mono text-[9px] tracking-wider uppercase',
                  f.chip.includes('terminal') || f.chip === 'success'
                    ? 'bg-foreground text-background'
                    : 'border border-edge text-muted-foreground'
                )}
              >
                {f.chip}
              </span>
            )}
            <p className="text-xs leading-relaxed text-muted-foreground">
              {f.note}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
